import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, FloppyDisk, Eye, Calendar, Image as ImageIcon, Desktop, DeviceMobile, DeviceTablet } from '@phosphor-icons/react';
import { toast } from 'sonner';
import EnhancedRichTextEditor from '../EnhancedRichTextEditor';
import MediaPickerInput from '../MediaPickerInput';
import { createSafeRichHTML } from '../../lib/sanitize';

const TEMPLATES = ['standard', 'landing', 'legal', 'faq', 'contact', 'about', 'fullwidth', 'blank'];
const CATEGORIES = ['information', 'legal', 'writer', 'reader', 'other'];

export default function PageEditorModal({ page, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState('content');
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop'); // desktop, tablet, mobile
  const [availablePages, setAvailablePages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    template: 'standard',
    category: 'information',
    status: 'draft',
    scheduled_at: '',
    // Visibility
    visibility: 'public',
    password: '',
    // Navigation
    parent_id: '',
    show_in_header: false,
    show_in_footer: false,
    show_in_sidebar: false,
    show_in_sitemap: true,
    show_in_search: true,
    // SEO
    seo_title: '',
    seo_description: '',
    seo_keywords: [],
    canonical_url: '',
    meta_robots: 'index, follow',
    // Open Graph
    og_title: '',
    og_description: '',
    og_image: '',
    og_type: 'website',
    // Twitter
    twitter_card: 'summary_large_image',
    twitter_title: '',
    twitter_description: '',
    twitter_image: '',
  });

  useEffect(() => {
    if (page) {
      setFormData({
        title: page.title || '',
        slug: page.slug || '',
        content: page.content || '',
        excerpt: page.excerpt || '',
        featured_image: page.featured_image || '',
        template: page.template || 'standard',
        category: page.category || 'information',
        status: page.status || 'draft',
        scheduled_at: page.scheduled_at || '',
        visibility: page.visibility || 'public',
        password: page.password || '',
        parent_id: page.parent_id || '',
        show_in_header: page.show_in_header || false,
        show_in_footer: page.show_in_footer || false,
        show_in_sidebar: page.show_in_sidebar || false,
        show_in_sitemap: page.show_in_sitemap ?? true,
        show_in_search: page.show_in_search ?? true,
        seo_title: page.seo_title || '',
        seo_description: page.seo_description || '',
        seo_keywords: page.seo_keywords || [],
        canonical_url: page.canonical_url || '',
        meta_robots: page.meta_robots || 'index, follow',
        og_title: page.og_title || '',
        og_description: page.og_description || '',
        og_image: page.og_image || '',
        og_type: page.og_type || 'website',
        twitter_card: page.twitter_card || 'summary_large_image',
        twitter_title: page.twitter_title || '',
        twitter_description: page.twitter_description || '',
        twitter_image: page.twitter_image || '',
      });
    }
    
    // Fetch available pages for parent dropdown
    const fetchPages = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${API}/api/admin/pages?limit=100`, {
          withCredentials: true,
        });
        setAvailablePages(res.data.pages || []);
      } catch (error) {
        console.error('Failed to load pages for dropdown');
      }
    };
    fetchPages();
  }, [page]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const autoGenerateSlug = () => {
    if (!formData.slug && formData.title) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      setFormData({ ...formData, slug });
    }
  };

  const handleSave = () => {
    if (!formData.title || !formData.slug) {
      toast.error('Title and slug are required');
      return;
    }
    onSave(formData);
  };

  const tabs = [
    { key: 'content', label: 'Content' },
    { key: 'visibility', label: 'Visibility' },
    { key: 'navigation', label: 'Navigation' },
    { key: 'seo', label: 'SEO & Meta' },
    { key: 'og', label: 'Social Sharing' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-background border border-border rounded-lg w-full max-w-5xl mx-4 shadow-xl">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between sticky top-0 bg-background z-10">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {page ? 'Edit Page' : 'Create New Page'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-lg text-sm hover:bg-muted"
            >
              <Eye size={16} /> {showPreview ? 'Hide' : 'Show'} Preview
            </button>
            {showPreview && (
              <div className="flex gap-1 border-l border-border pl-2">
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className={`p-1.5 rounded ${previewDevice === 'desktop' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  title="Desktop"
                >
                  <Desktop size={18} weight="bold" />
                </button>
                <button
                  onClick={() => setPreviewDevice('tablet')}
                  className={`p-1.5 rounded ${previewDevice === 'tablet' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  title="Tablet"
                >
                  <DeviceTablet size={18} weight="bold" />
                </button>
                <button
                  onClick={() => setPreviewDevice('mobile')}
                  className={`p-1.5 rounded ${previewDevice === 'mobile' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  title="Mobile"
                >
                  <DeviceMobile size={18} weight="bold" />
                </button>
              </div>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-muted rounded">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border px-6 flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
          {/* CONTENT TAB */}
          {activeTab === 'content' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Page Title <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    onBlur={autoGenerateSlug}
                    required
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="e.g., About Us"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    URL Slug <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono text-sm"
                    placeholder="about-us"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    URL: /page/{formData.slug || 'your-slug'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Excerpt</label>
                <textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Short description for previews and meta..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3">Page Content</label>
                <EnhancedRichTextEditor
                  content={formData.content}
                  onChange={(html) => setFormData({ ...formData, content: html })}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  💡 Use the enhanced editor: Tables, Colors, Task Lists, YouTube embeds, and more!
                </p>
              </div>

              <MediaPickerInput
                label="Featured Image"
                value={formData.featured_image}
                onChange={(url) => setFormData({ ...formData, featured_image: url })}
                accept="image"
                helpText="Recommended: 1200x630px for optimal social sharing"
              />
            </div>
          )}

          {/* VISIBILITY TAB */}
          {activeTab === 'visibility' && (
            <div className="space-y-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-purple-900">
                  <strong>Visibility:</strong> Control who can access this page.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3">Page Visibility</label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/30">
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={formData.visibility === 'public'}
                      onChange={handleInputChange}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-semibold">Public</p>
                      <p className="text-sm text-muted-foreground">Anyone can view this page</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/30">
                    <input
                      type="radio"
                      name="visibility"
                      value="private"
                      checked={formData.visibility === 'private'}
                      onChange={handleInputChange}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-semibold">Private</p>
                      <p className="text-sm text-muted-foreground">Only admins and editors can view</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/30">
                    <input
                      type="radio"
                      name="visibility"
                      value="password_protected"
                      checked={formData.visibility === 'password_protected'}
                      onChange={handleInputChange}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-semibold">Password Protected</p>
                      <p className="text-sm text-muted-foreground">Requires password to view</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/30">
                    <input
                      type="radio"
                      name="visibility"
                      value="members_only"
                      checked={formData.visibility === 'members_only'}
                      onChange={handleInputChange}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-semibold">Members Only</p>
                      <p className="text-sm text-muted-foreground">Only logged-in users can view</p>
                    </div>
                  </label>
                </div>
              </div>

              {formData.visibility === 'password_protected' && (
                <div>
                  <label className="block text-sm font-semibold mb-2">Page Password</label>
                  <input
                    type="text"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-border rounded-lg"
                    placeholder="Enter password"
                  />
                </div>
              )}
            </div>
          )}

          {/* NAVIGATION TAB */}
          {activeTab === 'navigation' && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-900">
                  <strong>Navigation:</strong> Choose where this page appears in site navigation.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3">Show in Navigation</label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.show_in_header}
                      onChange={(e) => setFormData({ ...formData, show_in_header: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-semibold">Header Navigation</p>
                      <p className="text-sm text-muted-foreground">Display in main header menu</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.show_in_footer}
                      onChange={(e) => setFormData({ ...formData, show_in_footer: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-semibold">Footer Navigation</p>
                      <p className="text-sm text-muted-foreground">Display in footer menu</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.show_in_sidebar}
                      onChange={(e) => setFormData({ ...formData, show_in_sidebar: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-semibold">Sidebar Navigation</p>
                      <p className="text-sm text-muted-foreground">Display in sidebar menu (if theme supports)</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.show_in_sitemap}
                      onChange={(e) => setFormData({ ...formData, show_in_sitemap: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-semibold">Include in Sitemap</p>
                      <p className="text-sm text-muted-foreground">Add to XML sitemap for search engines</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.show_in_search}
                      onChange={(e) => setFormData({ ...formData, show_in_search: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-semibold">Include in Search</p>
                      <p className="text-sm text-muted-foreground">Make searchable on the website</p>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Parent Page (Optional)</label>
                <select
                  name="parent_id"
                  value={formData.parent_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-border rounded-lg"
                >
                  <option value="">None (Top-level page)</option>
                  {availablePages
                    .filter(p => p.page_id !== page?.page_id) // Don't allow page to be its own parent
                    .map(p => (
                      <option key={p.page_id} value={p.page_id}>
                        {p.title}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Set a parent page to create hierarchical navigation and breadcrumbs
                </p>
              </div>
            </div>
          )}

          {/* SEO TAB */}
          {activeTab === 'seo' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>SEO Tip:</strong> Optimize these fields to improve search engine visibility.
                  If left blank, defaults will be used from page title and excerpt.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">SEO Title</label>
                <input
                  type="text"
                  name="seo_title"
                  value={formData.seo_title}
                  onChange={handleInputChange}
                  maxLength={60}
                  className="w-full px-3 py-2 border border-border rounded-lg"
                  placeholder="Page Title — Kavyalekh"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.seo_title.length}/60 characters (optimal: 50-60)
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">SEO Description</label>
                <textarea
                  name="seo_description"
                  value={formData.seo_description}
                  onChange={handleInputChange}
                  maxLength={160}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg resize-none"
                  placeholder="Brief description that appears in search results..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.seo_description.length}/160 characters (optimal: 150-160)
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">SEO Keywords</label>
                <input
                  type="text"
                  name="seo_keywords"
                  value={formData.seo_keywords.join(', ')}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      seo_keywords: e.target.value.split(',').map((k) => k.trim()).filter(Boolean),
                    })
                  }
                  className="w-full px-3 py-2 border border-border rounded-lg"
                  placeholder="keyword1, keyword2, keyword3"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Comma-separated keywords (not critical for modern SEO, but helpful for organization)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Canonical URL</label>
                  <input
                    type="url"
                    name="canonical_url"
                    value={formData.canonical_url}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-border rounded-lg"
                    placeholder="https://kavyalekh.com/page/about"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave blank to use default page URL
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Meta Robots</label>
                  <select
                    name="meta_robots"
                    value={formData.meta_robots}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-border rounded-lg"
                  >
                    <option value="index, follow">Index, Follow (Default)</option>
                    <option value="noindex, follow">No Index, Follow</option>
                    <option value="index, nofollow">Index, No Follow</option>
                    <option value="noindex, nofollow">No Index, No Follow</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* OPEN GRAPH / SOCIAL TAB */}
          {activeTab === 'og' && (
            <div className="space-y-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-purple-900">
                  <strong>Social Sharing:</strong> Control how this page appears when shared on
                  Facebook, Twitter, LinkedIn, and other social platforms.
                </p>
              </div>

              <h3 className="text-lg font-bold border-b border-border pb-2">Open Graph (Facebook, LinkedIn)</h3>

              <div>
                <label className="block text-sm font-semibold mb-2">OG Title</label>
                <input
                  type="text"
                  name="og_title"
                  value={formData.og_title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-border rounded-lg"
                  placeholder="Defaults to page title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">OG Description</label>
                <textarea
                  name="og_description"
                  value={formData.og_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg resize-none"
                  placeholder="Defaults to excerpt"
                />
              </div>

              <MediaPickerInput
                label="OG Image"
                value={formData.og_image}
                onChange={(url) => setFormData({ ...formData, og_image: url })}
                accept="image"
                helpText="Recommended: 1200x630px (1.91:1 ratio). Defaults to featured image."
              />

              <div>
                <label className="block text-sm font-semibold mb-2">OG Type</label>
                <select
                  name="og_type"
                  value={formData.og_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-border rounded-lg"
                >
                  <option value="website">Website</option>
                  <option value="article">Article</option>
                  <option value="blog">Blog</option>
                </select>
              </div>

              <h3 className="text-lg font-bold border-b border-border pb-2 mt-8">Twitter Card</h3>

              <div>
                <label className="block text-sm font-semibold mb-2">Twitter Card Type</label>
                <select
                  name="twitter_card"
                  value={formData.twitter_card}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-border rounded-lg"
                >
                  <option value="summary">Summary</option>
                  <option value="summary_large_image">Summary Large Image</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Twitter Title</label>
                <input
                  type="text"
                  name="twitter_title"
                  value={formData.twitter_title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-border rounded-lg"
                  placeholder="Defaults to OG title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Twitter Description</label>
                <textarea
                  name="twitter_description"
                  value={formData.twitter_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg resize-none"
                  placeholder="Defaults to OG description"
                />
              </div>

              <MediaPickerInput
                label="Twitter Image"
                value={formData.twitter_image}
                onChange={(url) => setFormData({ ...formData, twitter_image: url })}
                accept="image"
                helpText="Defaults to OG image"
              />
            </div>
          )}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Template</label>
                  <select
                    name="template"
                    value={formData.template}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-border rounded-lg"
                  >
                    {TEMPLATES.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-border rounded-lg"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c.charAt(0).toUpperCase() + c.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-border rounded-lg"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              {formData.status === 'scheduled' && (
                <div>
                  <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                    <Calendar size={16} /> Schedule Publishing Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    name="scheduled_at"
                    value={formData.scheduled_at}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-border rounded-lg"
                  />
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Publishing Tips:</h4>
                <ul className="text-sm text-amber-900 space-y-1">
                  <li>• <strong>Draft:</strong> Save for later, not visible to public</li>
                  <li>• <strong>Published:</strong> Live immediately on the website</li>
                  <li>• <strong>Scheduled:</strong> Auto-publish at specified date/time</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Live Preview */}
        {showPreview && (
          <div className="border-t border-border bg-muted/30 p-6">
            <div className="flex items-center justify-center">
              <div
                className={`bg-white border-4 border-gray-800 rounded-lg overflow-hidden shadow-2xl transition-all ${
                  previewDevice === 'desktop'
                    ? 'w-full max-w-6xl h-[600px]'
                    : previewDevice === 'tablet'
                    ? 'w-[768px] h-[600px]'
                    : 'w-[375px] h-[667px]'
                }`}
              >
                <div
                  className="w-full h-full overflow-y-auto p-8 prose prose-sm max-w-none"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  <h1 style={{ fontFamily: 'Cormorant Garamond, serif' }}>{formData.title || 'Untitled Page'}</h1>
                  {formData.excerpt && <p className="text-lg text-gray-600">{formData.excerpt}</p>}
                  <div dangerouslySetInnerHTML={createSafeRichHTML(formData.content || '<p>Start writing content...</p>')} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="border-t border-border px-6 py-4 flex items-center justify-between sticky bottom-0 bg-background">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90"
          >
            <FloppyDisk size={20} weight="fill" />
            {page ? 'Update Page' : 'Create Page'}
          </button>
        </div>
      </div>
    </div>
  );
}
